import httpClient from './http-client'

/**
 * User-granted, time-boxed email access (lighthouse-backend `app/routers/support_access.py`). "View as" works
 * without one and opens the support view (no Gmail/Outlook content); while the customer has approved a request,
 * `POST /lightbase/impersonate/{id}` opens a full session instead. Lightbase files the request and the customer
 * gets a push plus a live prompt in the app.
 */
export type SupportAccessStatus = 'pending' | 'approved' | 'denied' | 'expired' | 'revoked'

export interface SupportAccessRequest {
  id: string
  status: SupportAccessStatus
  reason: string | null
  requested_by_name: string | null
  duration_minutes: number
  created_at: string
  /** The customer must answer before this (15 minutes after the request). */
  request_expires_at: string
  /** Set once approved: access ends at this time. */
  granted_until: string | null
  user_id?: string
  org_id?: string | null
  requested_by?: string | null
  responded_at?: string | null
}

export interface SupportAccessState {
  pending: SupportAccessRequest | null
  active_grant: SupportAccessRequest | null
}

export const SUPPORT_ACCESS_DURATIONS = [15, 30, 60, 120, 240] as const
export const DEFAULT_SUPPORT_ACCESS_DURATION = 60

export const supportAccessApi = {
  get: (userId: string) => httpClient.get<SupportAccessState>(`/lightbase/support-access/${userId}`),
  request: (userId: string, reason: string, durationMinutes: number) =>
    httpClient.post<SupportAccessRequest>(`/lightbase/support-access/${userId}`, {
      reason,
      duration_minutes: durationMinutes,
    }),
}

/** A readable message from a backend error body (`{"detail": {"error": code}}`, a string, or validation errors). */
export function supportAccessErrorMessage(data: unknown, fallback: string): string {
  const detail = data && typeof data === 'object' && 'detail' in data ? (data as { detail: unknown }).detail : null
  const code =
    detail && typeof detail === 'object' && !Array.isArray(detail) && 'error' in detail
      ? String((detail as { error: unknown }).error)
      : null
  switch (code) {
    case 'support_access_required':
      return 'The customer has to allow email access first.'
    case 'user_not_found':
      return 'This customer no longer exists.'
    case 'reason_required':
      return 'Add a reason: the customer sees it in the request.'
    case 'staff_account':
      return 'Staff accounts can’t be sent support access requests.'
    default:
      break
  }
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0]
    if (first && typeof first === 'object' && 'msg' in first) return String((first as { msg: unknown }).msg)
  }
  return code ? `${fallback} (${code})` : fallback
}
